<template lang="pug">
#event-registrations
  .page-controls
    h1.is-size-4 {{ event.title }}
    b-field(grouped)
      b-field(expanded)
        p.control
          b-dropdown(v-model="filters.query_field")
            a.button(slot="trigger")
              b-icon(icon="magnify")
              span Search by {{ queryField.label }}
            b-dropdown-item(
              v-for="field in formFields"
              :key="field.id"
              :value="field.id"
              @click="filterFieldClickHandler"
            ) {{ field.label }}
        p.control(style="flex-grow:1;")
          search-field(
            icon=""
            :value="filters.query"
            @change="setQuery"
            @reset="resetQuery"
            expanded
          )
      p.control
        b-dropdown(
          position="is-bottom-left"
          ref="fieldVisibilityDropdown"
          :value="featuredFormFieldIds"
          multiple
        )
          a.button(slot="trigger")
            b-icon(icon="table-plus")
            span Table Fields
            b-icon(icon="chevron-down")
          b-dropdown-item(
            v-for="field in formFields"
            :key="field.id"
            :value="field.id"
            @click="toggleFormField(field)"
          ) {{ field.label }}
      p.control
        b-dropdown(position="is-bottom-left")
          a.button(slot="trigger")
            span Actions
            b-icon(icon="chevron-down")
          b-dropdown-item(has-link)
            a(:href="downloadLink")
              b-icon(icon="download")
              span Download CSV
          b-dropdown-item(has-link)
            a(@click="getPage(1)")
              b-icon(icon="refresh")
              span Refresh
          b-dropdown-item(has-link)
            a(:href="`http://${account.host}/${event.type}s/${event.key}`" target="_blank")
              b-icon(icon="calendar-text")
              span View Event Page
          b-dropdown-item(has-link)
            a.has-text-danger(@click="confirmReset")
              b-icon(icon="close")
              span Delete all Sign Ups
    .registration-stats.columns
      .column.is-one-third
        .card
          .card-content.has-text-centered
            p.has-text-grey-light # of Sign Ups
            p.is-size-2 {{ stats.registration_count }}
      .column.is-one-third
        .card
          .card-content.has-text-centered
            p.has-text-grey-light # of Orders
            p.is-size-2 {{ stats.order_count }}
      .column.is-one-third
        .card
          .card-content.has-text-centered
            p.has-text-grey-light Fees Collected
            p.is-size-2 {{ stats.total_amount | cents }}

  spinner(v-if="settings.loading")
  div(v-else)
    b-table(
      :data="registrations"
      :current-page="pagination.page"
      :per-page="pagination.per"
      :total="pagination.total"
      @click="openRegistration"
      @page-change="getPage"
      backend-pagination
      hoverable
      paginated
    )
      template(slot-scope="props")
        b-table-column(
          v-for="field in formFields"
          :label="field.label"
          :key="field.id"
          :visible="field.is_featured"
        )
          span(v-if="valueFor(props.row, field)") {{ valueFor(props.row, field) }}
          span.has-text-grey-light(v-else) NA
        b-table-column(label="Registration Date") {{ props.row.created_at | date('MMMM D, YYYY') }}
        b-table-column(
          label="Amount"
          :visible="event.payment_required"
        ) {{ props.row.amount | cents }}
        b-table-column(
          label="Order #"
          :visible="event.payment_required"
        )
          a(
            v-if="props.row._embedded.order"
            @click.stop="openOrder(props.row._embedded.order)"
          ) {{ props.row._embedded.order.number }}
          span.has-tex-grey-light(v-else) NA

      template(slot="empty")
        .notification.has-text-centered No Sign Ups
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { mapGetters } from 'vuex';
import EventRegistrationModal from '@/components/EventRegistrationModal';
import OrderModal from '@/components/OrderModal';
import SearchField from '@/components/form/SearchField';
import Spinner from '@/components/Spinner';
import * as actions from '@/store/types/actions';
import * as getters from '@/store/types/getters';
import * as mutations from '@/store/types/mutations';

export default {
  name: 'event-registration',
  components: {
    EventRegistrationModal,
    OrderModal,
    SearchField,
    Spinner,
  },
  computed: {
    ...mapGetters({
      account: getters.GET_ACCOUNT,
      event: getters.GET_EVENT,
      form: getters.GET_FORM,
      refresh: getters.GET_EVENT_REGISTRATION_REFRESH,
      registrations: getters.GET_EVENT_REGISTRATIONS,
    }),
    downloadLink() {
      const { id } = this.event;
      const token = localStorage.getItem('token');
      return `${process.env.API_ENDPOINT}/downloads/events/${id}/registrations?token=${token}`;
    },
    formFields() {
      let fields = [];
      if (this.form.id) {
        this.form.blocks.forEach((block) => {
          fields = fields.concat(block.form_fields);
        });
      }
      return fields;
    },
    featuredFormFields() {
      return _.filter(this.formFields, { is_featured: true });
    },
    featuredFormFieldIds() {
      return _.map(this.featuredFormFields, 'id');
    },
    queryField() {
      if (!this.filters.query_field) return {};
      return _.find(this.formFields, { id: this.filters.query_field });
    },
  },
  async created() {
    this.activeTab = this.tab;
    this.settings.loading = true;
    await this.$store.dispatch(actions.DO_GET_EVENT, { id: this.id });
    if (this.event.form_id) {
      await this.$store.dispatch(actions.DO_GET_FORM, {
        id: this.event.form_id,
        zoom: 'block',
      });
      const field = this.formFields[0];
      if (field) {
        this.filters.query_field = field.id;
      }
      this.getPage(this.page);
    }
  },
  data() {
    return {
      filters: {
        query: null,
        query_field: null,
      },
      pagination: {
        page: 1,
        per: 10,
        total: 0,
      },
      searchField: {},
      settings: {
        loading: false,
        downloading: false,
        resetting: false,
      },
      stats: {
        registration_count: 0,
        order_count: 0,
        total_amount: 0,
      },
    };
  },
  methods: {
    confirmReset() {
      this.$buefy.dialog.confirm({
        title: 'Are you sure?',
        message: `Deleting all the sign ups for <b>${this.event.title}</b> cannot be undone and all associated orders will be deleted.`,
        confirmText: 'Yes, Reset',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.reset(),
      });
    },
    filterFieldClickHandler() {
      if (this.filters.query) this.$store.commit(mutations.SET_EVENT_REGISTRATION_REFRESH, true);
    },
    async getPage(page) {
      this.settings.loading = true;
      this.pagination.page = page;
      const response = await this.$store.dispatch(actions.DO_GET_EVENT_REGISTRATIONS, {
        id: this.event.id,
        page,
        per: this.pagination.per,
        query: this.filters.query,
        query_field: this.filters.query_field,
        zoom: 'order',
      });
      this.stats = response.stats;
      this.settings.loading = false;
      this.pagination.total = response.record_count;
      this.$store.commit(mutations.SET_EVENT_REGISTRATION_REFRESH, false);
      return response;
    },
    async openOrder(order) {
      await this.$store.dispatch(actions.DO_GET_ORDER, {
        id: order.id,
        zoom: 'registrations',
      });
      this.$buefy.modal.open({
        parent: this,
        hasModalCard: true,
        component: OrderModal,
      });
    },
    async openRegistration(registration) {
      await this.$store.dispatch(actions.DO_GET_EVENT_REGISTRATION, {
        id: registration.id,
      });
      this.$buefy.modal.open({
        parent: this,
        hasModalCard: true,
        component: EventRegistrationModal,
      });
    },
    async reset() {
      await Promise.all([
        this.$store.dispatch(actions.DO_DELETE_EVENT_REGISTRATIONS, { id: this.event.id }),
        this.$store.dispatch(actions.DO_DELETE_EVENT_ORDERS, { id: this.event.id }),
      ]);
      this.$store.commit(mutations.SET_APP_SNACKBAR, {
        type: 'is-success',
        message: 'Sign ups reset',
      });
      this.$store.commit(mutations.SET_EVENT_REGISTRATION_REFRESH, true);
    },
    resetQuery() {
      this.filters.query = null;
      this.$store.commit(mutations.SET_EVENT_REGISTRATION_REFRESH, true);
    },
    setQuery(query) {
      this.filters.query = query;
      this.$store.commit(mutations.SET_EVENT_REGISTRATION_REFRESH, true);
    },
    async toggleFormField(field) {
      this.$refs.fieldVisibilityDropdown.toggle();
      const val = !field.is_featured;
      this.$store.commit(mutations.SET_FIELD_FOR_BLOCK_FIELD, {
        block_id: field.form_block_id,
        field_id: field.id,
        key: 'is_featured',
        val,
      });
      const payload = {
        id: field.id,
        is_featured: val,
      };
      await this.$store.dispatch(actions.DO_UPDATE_FORM_FIELD, payload);
    },
    valueFor(registration, field) {
      if (registration.registration_fields.length === 0) return null;
      const regField = _.find(registration.registration_fields, { form_field_id: field.id });
      if (regField === undefined) return null;
      let value = regField.value;
      switch (regField.field_type) {
        case 'date':
          if (value !== undefined) {
            value = moment(value).format('MMMM D, YYYY');
          }
          break;
        case 'checkbox':
          value = value === 'true' ? 'Yes' : 'No';
          break;
        default:
      }
      return value;
    },
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    page: {
      type: Number,
      default: 1,
    },
    tab: {
      type: Number,
      default: 0,
    },
  },
  watch: {
    refresh(val) {
      if (val) this.getPage(1);
    },
  },
};
</script>

<style lang="scss">
#event-registrations {
  .page-controls {
    h1 {
      margin-bottom: 0.75rem;
    }
  }
  .dropdown-menu {
    .dropdown-content {
      a {
        display: flex;
        align-items: center;
        &:focus {
          outline: none;
        }
        .icon {
          margin-right: 0.35rem;
        }
      }
    }
  }
  .registration-stats {
    margin-bottom: 0.75rem;
    .card {
      box-shadow: none;
      border: 1px solid #dbdbdb;
      border-radius: 4px;
    }
  }
  table.table {
    a {
      color: $theme-primary;
      text-decoration: underline;
    }
    tr {
      cursor: pointer;
      th, td {
        &:first-child {
          padding-left: 1rem;
        }
        &:last-child {
          padding-right: 1rem;
        }
      }
    }
  }
  .table-wrapper + .level {
    margin-right: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/EventRegistrations.vue